import type { Place, SatellitePlace, SearchAddressResult } from '@types';
import RerouteModal from 'components/rerouteModal/rerouteModal';
import SectionCallToAction from 'components/sections/sectionAppBanner/SectionCallToAction';
import { useFormattedTitle } from 'hooks/useFormattedTitle';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { getAnalyticsService } from 'services/analyticsService';

import { AppContext } from '../../../context/app';
import { ThemeType } from '../../../context/types';
import { useAddParamsAndNavigate } from '../../../hooks/useAddParamsAndNavigate';
import { useCampusComparison } from '../../../hooks/useCampusComparison';
import { useClearParamsAndNavigate } from '../../../hooks/useClearParamsAndNavigate';
import useIsMobile from '../../../hooks/useIsMobile';
import { useKioskManager } from '../../../hooks/useKioskManager';
import { useNavigationManager } from '../../../hooks/useNavigationManager';
import { useNavStartingPlaceLabel } from '../../../hooks/useNavStartingPlaceLabel';
import { useNetworkManager } from '../../../hooks/useNetworkManager';
import { ROUTES } from '../../../utils/routes';
import { isGozioPlace } from '../../../utils/typeGuards';
import ChangeStartingLocationModal from '../../changeStartingLocationModal/ChangeStartingLocationModal';
import CurrentLocation from '../../shared/currentLocation/CurrentLocation';
import Icon from '../../shared/icon/Icon';
import { IconType } from '../../shared/icon/types';
import LocationSelector from '../../shared/locationSelector/LocationSelector';
import { NavPlaceholder } from '../../shared/navPlaceHolder/NavPlaceHolder';
import PlacesAutocomplete from '../../shared/placesAutocomplete/PlacesAutocomplete';
import { NavDestinationSearch } from '../../shared/searchInput/NavDestinationSearch';
import PlacesSearchInput from '../../shared/searchInput/PlacesSearchInput';
import { MapsLabels } from '../../shared/types';
import styles from './sectionNavigationForm.module.scss';

type ReroutePlace = Place | SatellitePlace | null;
interface IRerouteData {
  originalDestination: ReroutePlace;
  rerouteDestination: ReroutePlace;
}

const SectionNavigationForm: React.FC = () => {
  const { t } = useTranslation('common');

  const {
    setDirections,
    setNavStartingPlace,
    setNavDestinationPlace,
    navStartingPlace,
    navDestinationPlace,
    setAddressSearchResult,
    setHideNavCTASection,
    setLocationSearchText,
    locationSearchText,
    mapLoading,
    hideNavCTASection,
  } = useContext(AppContext);

  const [rerouteData, setRerouteData] = useState<IRerouteData | null>(null);

  const analyticsService = useMemo(() => getAnalyticsService(), []);

  const { kioskMode } = useKioskManager();

  const { geoNodeIsLoading, startingGeoNodeIsLoading, startingGeoNode } =
    useNavigationManager();
  const { siteName, placesData, satellitePlacesData } = useNetworkManager();

  const destinationTitle = useFormattedTitle(
    'campusNavigation.form.destinationDescription',
    siteName || '',
  );

  const destinationPlaceholder = useFormattedTitle(
    'campusNavigation.form.destinationPlaceholder',
    siteName || '',
  );

  const [setting, setSetting] = useState<'starting' | 'destination' | null>(
    'starting',
  );
  const [startingPlaceLabel, setStartingPlaceLabel] = useState<string>('');
  const [destinationPlaceLabel, setDestinationPlaceLabel] =
    useState<string>('');

  const isMobile = useIsMobile();
  const navStartingPlaceLabel = useNavStartingPlaceLabel();
  const addParamsAndNavigate = useAddParamsAndNavigate();
  const clearParamsAndNavigate = useClearParamsAndNavigate();

  useEffect(() => {
    if (navStartingPlaceLabel) setStartingPlaceLabel(navStartingPlaceLabel);
  }, [navStartingPlaceLabel]);

  const handleStartingInputChange = (value: string | undefined) => {
    setStartingPlaceLabel(value || '');
    setLocationSearchText(value);
  };
  const handleStartingInputClear = () => {
    clearParamsAndNavigate([
      'startingid',
      'kioskId',
      'startingLat',
      'startingLon',
      'startingAddress',
    ]);
    setNavStartingPlace(undefined);
    setAddressSearchResult(undefined);
    setStartingPlaceLabel('');
    handleStartingInputChange('');
  };

  const handleDestinationInputChange = (value: string | undefined) => {
    setDestinationPlaceLabel(value || '');
    setLocationSearchText(value);
  };

  const handleDestinationInputClear = () => {
    setNavDestinationPlace(undefined);
    setDestinationPlaceLabel('');
    setLocationSearchText(undefined);
    setDirections(undefined);
    clearParamsAndNavigate(['destinationid']);
  };

  useEffect(() => {
    if (!startingGeoNodeIsLoading && startingGeoNode && navStartingPlace) {
      const { lat, lon } = startingGeoNode.data.payload.geoInfo;
      analyticsService.track(analyticsService.events.SetStartingLocation, {
        destinationId: navStartingPlace.id,
        lat,
        lon,
      });
    }
  }, [
    navStartingPlace,
    analyticsService,
    startingGeoNodeIsLoading,
    startingGeoNode,
    startingPlaceLabel,
  ]);

  const handleSetLocation = (
    location: Place | SatellitePlace,
    overrideReroute: boolean = false,
  ) => {
    if (setting === 'destination' || overrideReroute) {
      analyticsService.track(analyticsService.events.SetDestination, {
        destinationId: location.id,
      });
      analyticsService.track(
        analyticsService.events.DestinationSearchSelected,
        {
          destinationId: location.id,
          value: locationSearchText || '',
        },
      );

      if (location) {
        if (destinationPlaceLabel !== location.name) {
          setDestinationPlaceLabel(location.name);
          setDirections(undefined);
          addParamsAndNavigate([
            { name: 'destinationid', value: location.id.toString() },
          ]);
        }
      }
    } else if (setting === 'starting') {
      if (location) {
        analyticsService.track(
          analyticsService.events.StartingLocationSearchSelected,
          {
            destinationId: location.id,
            value: startingPlaceLabel,
          },
        );
        setDirections(undefined);
        setStartingPlaceLabel(location.name);
        addParamsAndNavigate([
          { name: 'startingid', value: location.id.toString() },
        ]);
      }
    }
  };

  const showLocationSelector = useMemo(
    () =>
      !!(setting === 'starting' && startingPlaceLabel) ||
      !!(setting === 'destination' && destinationPlaceLabel),
    [destinationPlaceLabel, setting, startingPlaceLabel],
  );

  useEffect(() => {
    setHideNavCTASection(!!startingPlaceLabel || !!destinationPlaceLabel);
  }, [startingPlaceLabel, destinationPlaceLabel, setHideNavCTASection]);

  useEffect(() => {
    if (!navStartingPlace) {
      return;
    }
    setSetting(null);

    if (!navDestinationPlace) {
      setSetting('destination');
    }
  }, [navStartingPlace, navDestinationPlace]);

  useEffect(() => {
    if (!navDestinationPlace) {
      setDestinationPlaceLabel('');
    } else if ('name' in navDestinationPlace && navDestinationPlace.name) {
      setDestinationPlaceLabel(navDestinationPlace.name);
      const redirectPlace =
        placesData?.find(
          (l) => l.id === navDestinationPlace.nav_redirect_place_id,
        ) ??
        satellitePlacesData?.find(
          (l) => l.id === navDestinationPlace.nav_redirect_place_id,
        );
      if (redirectPlace) {
        setRerouteData({
          originalDestination: navDestinationPlace,
          rerouteDestination: redirectPlace,
        });
        setSetting('destination');
      } else if (
        (navDestinationPlace.disclaimer_text &&
          !navDestinationPlace.nav_instruction) ||
        (navDestinationPlace.nav_instruction &&
          !navDestinationPlace.nav_instruction.redirect_place_id)
      ) {
        setSetting('destination');
        setRerouteData({
          originalDestination: navDestinationPlace,
          rerouteDestination: null,
        });
      }
    }
  }, [navDestinationPlace, placesData, satellitePlacesData]);

  const { isDifferentCampus } = useCampusComparison();

  const handleStartingLocationUpdate = (
    // eslint-disable-next-line no-undef
    place?: SearchAddressResult | undefined,
  ) => {
    addParamsAndNavigate([
      { name: 'startingid', value: null },
      { name: 'startingLat', value: place?.position?.[0].toString() || '' },
      { name: 'startingLon', value: place?.position?.[1].toString() || '' },
      { name: 'startingAddress', value: place?.address || '' },
    ]);
    if (place) {
      analyticsService.track(analyticsService.events.SetStartingLocation, {
        address: place?.address,
        lat: place?.position?.[0],
        lon: place?.position?.[1],
      });
    }
    setNavStartingPlace(place);
  };

  const handleStartingInputFocus = () => {
    if (
      navStartingPlaceLabel ===
        t('campusNavigation.location.currentLocation') ||
      navStartingPlaceLabel === MapsLabels.MY_LOCATION
    ) {
      setStartingPlaceLabel('');
    }
    setSetting('starting');
  };

  const [locationSearchResults, setLocationSearchResults] = useState<{
    [key: string]: any;
  }>({});

  const [mapsSearchResults, setMapsSearchResults] = useState<
    number | undefined
  >();

  useEffect(() => {
    const { setting, value, results: locationResults } = locationSearchResults;
    if (setting === 'destination') {
      analyticsService.track(analyticsService.events.DestinationSearch, {
        value,
        results: locationResults,
      });
    }
    if (setting === 'starting' && mapsSearchResults) {
      analyticsService.track(analyticsService.events.StartingLocationSearch, {
        value,
        addressResults: mapsSearchResults,
        locationResults: locationResults,
      });
    }
  }, [analyticsService, locationSearchResults, mapsSearchResults]);

  const showAutocomplete = useMemo(
    () => startingPlaceLabel.length > 1,
    [startingPlaceLabel],
  );

  const isMandatoryRerouteWithNoMessage = useMemo(
    () =>
      rerouteData?.originalDestination?.nav_instruction?.accept_only ===
        false &&
      rerouteData?.originalDestination?.nav_instruction?.redirect_place_id &&
      (rerouteData?.originalDestination?.nav_instruction?.message === '' ||
        !rerouteData?.originalDestination?.nav_instruction?.message),
    [rerouteData],
  );

  const showRerouteModal = useMemo(
    () => rerouteData && !isMandatoryRerouteWithNoMessage,
    [rerouteData, isMandatoryRerouteWithNoMessage],
  );

  useEffect(() => {
    if (showRerouteModal) {
      setSetting(null);
    }
  }, [showRerouteModal]);

  return !kioskMode &&
    (!navStartingPlaceLabel || navStartingPlaceLabel !== '') ? (
    <>
      {isGozioPlace(navStartingPlace) && (
        <ChangeStartingLocationModal
          isOpen={isDifferentCampus}
          onSubmit={handleStartingLocationUpdate}
          onClose={handleStartingInputClear}
          selectedStartingLocation={navStartingPlace as Place}
        />
      )}
      <div className={styles.sectionNavigationForm}>
        <div className={styles.navFormBlock}>
          <div className={styles.iconColumn}>
            <Icon icon={IconType.START_INDICATOR} />
            <Icon icon={IconType.MORE_VERT} color="#dfe5e8" />
            <Icon icon={IconType.LOCATION} theme={ThemeType.PRIMARY} />
          </div>
          <div className={styles.inputColumn}>
            <div className={styles.inputBlock}>
              <span className={styles.sectionNavigationFormLabel}>
                {t('campusNavigation.form.startingLocation')}
              </span>
              <PlacesSearchInput
                placeholder={t('campusNavigation.form.searchPlaceholder')}
                onFocus={handleStartingInputFocus}
                value={startingPlaceLabel || ''}
                onChange={handleStartingInputChange}
                onClear={handleStartingInputClear}
              />
            </div>
            <div className={styles.inputBlock}>
              <span className={styles.sectionNavigationFormLabel}>
                {t('campusNavigation.form.destination')}
              </span>
              <NavDestinationSearch
                destinationPlaceLabel={destinationPlaceLabel}
                placeholder={destinationPlaceholder}
                onFocus={() => setSetting('destination')}
                onChange={handleDestinationInputChange}
                onCancel={handleDestinationInputClear}
              />
            </div>
          </div>
        </div>
        {!showLocationSelector && <CurrentLocation />}
        <div className={styles.navFormColBlock}>
          {setting === 'starting' && startingPlaceLabel && !mapLoading && (
            <span className={styles.boldSectionHeader}>
              {t('campusNavigation.form.selectStartingLocation')}
            </span>
          )}
          {setting === 'destination' &&
            destinationPlaceLabel &&
            !rerouteData?.rerouteDestination && (
              <span className={styles.boldSectionHeader}>
                {t('campusNavigation.form.setDestination')}
              </span>
            )}
          {setting === 'destination' && !destinationPlaceLabel && (
            <>
              <p className={styles.description}>{destinationTitle}</p>
              <p className={styles.description}>
                {t('campusNavigation.location.contents.exploreCampus')}{' '}
                <NavLink
                  to={{
                    pathname: ROUTES.CAMPUS_DIRECTORY,
                    search: window.location.search,
                  }}
                >
                  {t('header.campusDirectory')}
                </NavLink>
              </p>
            </>
          )}
          {setting === 'starting' && startingPlaceLabel && showAutocomplete && (
            <PlacesAutocomplete
              input={startingPlaceLabel}
              setSearchResults={setMapsSearchResults}
              onSelect={(location) => {
                if (navStartingPlace?.address === startingPlaceLabel) {
                  window.location.reload();
                }
              }}
            />
          )}
          {showLocationSelector &&
            setting &&
            !rerouteData?.rerouteDestination && (
              <LocationSelector
                onSetLocation={handleSetLocation}
                setting={setting}
                setSearchResults={setLocationSearchResults}
              />
            )}
        </div>
      </div>

      {/*Show loading when the user is on Mobile, there is a starting location set, and the user selects a destination*/}
      {isMobile && geoNodeIsLoading && navStartingPlace && (
        <div className={styles.placeholderCenter}>
          <NavPlaceholder
            mapLoading={true}
            title={t('labels.loading')}
            subtitle={t('labels.delay')}
          />
        </div>
      )}
      <SectionCallToAction
        origin="CampusNavigation"
        hideCallToAction={!!navStartingPlace || !!hideNavCTASection}
      />
      {showRerouteModal && rerouteData && (
        <RerouteModal
          isOpen={!!showRerouteModal}
          onContinue={(location) => {
            setRerouteData(null);
            if (location) {
              if (navDestinationPlace?.id !== location.id) {
                handleSetLocation(location, true);
              }
            }
          }}
          originalDestination={rerouteData.originalDestination}
          rerouteDestination={rerouteData.rerouteDestination}
          onSubmit={(location) => {
            setRerouteData(null);
            if (location) {
              if (navDestinationPlace?.id !== location.id) {
                handleSetLocation(location, true);
              }
            }
          }}
        />
      )}
    </>
  ) : (
    <></>
  );
};

export default SectionNavigationForm;
